import { create } from "zustand";
import type { 
  TExpTemp,
  TTask,
  TImgEditorOptions,
  TVideoEditorOptions,
  TSign,
  TTaskOptions,
  TSafariInfo,
} from "@/types";
import { 
  APICode, 
  COMMUNITY_EXP_TEMP_TITLE_PREFFIX, 
  CUS_EXP_TEMP_TITLE_PREFFIX, 
  CollectStatus, 
  ExpTempType, 
  IMG_MAX_DEMENSION, 
  LocalStorageKey, 
  PROJECT_TYPE, 
  TaskStatus, 
  VIDEO_MAX_DEMENSION 
} from "@/constants";
import { 
  getPresetExpTempList, 
  getSignUrl, 
  getTask, 
  postCollect, 
  postTask, 
  putFileTo 
} from "@/api";
import { 
  getBasicInfoFromVideo, 
  getCommunityExpTempTitle, 
  getCusExpTempTitle, 
  getExtension, 
  getFirstFrame, 
  getImgDimensions, 
  isLocalUrl, 
  scaleImage 
} from "@/utils";
import { useUserStore } from "@/stores";
import FTAnalytic from "@/analyze/FTAnalytic";
import MaiDianUtils from "@/utils/MaiDianUtils";

type States = {
  sourceImgFile: File | null,
  sourceImgUrl: string;
  sourceImgBlobUrl: string,
  sourceImgUploading: boolean;
  communityExpTempList: TExpTemp[];
  presetExpTempList: TExpTemp[];
  cusExpTempList: TExpTemp[];
  cusExpTempUploading: boolean;
  task: TTask | null;
  generating: boolean;
  activeExpTempType: ExpTempType;
  activeExpTempIndex: number;
  imgEditorOptions: TImgEditorOptions;
  videoEditorOptions: TVideoEditorOptions;
  isMobile: boolean | undefined;
  removeTipVisible: boolean;
  moreExpTempVisible: boolean;
  isMute: boolean;
  isPlayResult: boolean;
  safariInfo: TSafariInfo | null;
  collectLoading: boolean;
}

type Getters = {
  getters: {
    activeExpTemp: TExpTemp | null;
    handling: boolean;
  };
}

type Mutations = {
  setSourceImgFile: (value: File) => void;
  setSourceImgUrl: (value: string) => void;
  setSourceImgBlobUrl: (value: string) => void;
  setSourceImgUploading: (value: boolean) => void;
  setPresetExpTempList: (value: TExpTemp[]) => void;
  setCusExpTempList: (value: TExpTemp[]) => void;
  setCusExpTempUploading: (value: boolean) => void;
  setTask: (value: TTask | null) => void;
  setGenerating: (value: boolean) => void;
  setActiveExpTempType: (value: ExpTempType) => void;
  setActiveExpTempIndex: (value: number) => void;
  setImgEditorOptions: (value: TImgEditorOptions) => void;
  setVideoEditorOptions: (value: TVideoEditorOptions) => void;
  setIsMobile: (value: boolean | undefined) => void;
  setRemoveTipVisible: (value: boolean) => void;
  setMoreExpTempVisible: (value: boolean) => void;
  setIsMute: (value: boolean) => void;
  setIsPlayResult: (value: boolean) => void;
  setSafariInfo: (value: TSafariInfo) => void;
}

type Actions = {
  reset: () => void;
  fetchPresetExpTempList: () => Promise<void>;
  fetchSignUrl: (extension: string) => Promise<TSign>;
  upload: (file: File, url: string) => Promise<void>;
  submitTask: (options: TTaskOptions) => Promise<TTask>;
  fetchTask: () => Promise<TTask>;
  generate: () => Promise<void | true>;
  loopTask: (loopQuantity?: number, failedCallback?: () => void) => Promise<void>;
  addCusExpTemp: (file: File) => void;
  updatePlayState: (isPlay: boolean, target?: TExpTemp) => void;
  showRemoveTip: () => void;
  closeVideoEditor: (option?: { task: Promise<File>, sourceVideo: File }) => Promise<void>;
  logoutCallback: () => void;
  applyPresetExp: () => void;
  collect: () => Promise<CollectStatus>;
}

const initialState: States = {
  sourceImgFile: null,
  sourceImgUrl: "",
  sourceImgBlobUrl: "",
  sourceImgUploading: false,
  communityExpTempList: [],
  presetExpTempList: [],
  cusExpTempList: [],
  cusExpTempUploading: false,
  task: null,
  generating: false,
  activeExpTempType: ExpTempType.Preset,
  activeExpTempIndex: 0,
  imgEditorOptions: {
    show: false,
  },
  videoEditorOptions: {
    show: false,
  },
  isMobile: undefined,
  removeTipVisible: false,
  moreExpTempVisible: false,
  isMute: true,
  isPlayResult: false,
  safariInfo: null,
  collectLoading: false,
}

const useCommonStore = create<States & Getters & Mutations & Actions>((set, get) => ({
  // states
  ...initialState,
  
  // getters
  getters: {
    get activeExpTemp () {
      const { activeExpTempIndex, activeExpTempType, communityExpTempList, presetExpTempList, cusExpTempList } = get();
      if (activeExpTempType === ExpTempType.Community) {
        return communityExpTempList[activeExpTempIndex];
      } else if (activeExpTempType === ExpTempType.Preset) {
        return presetExpTempList[activeExpTempIndex];
      } else if (activeExpTempType === ExpTempType.Cus) {
        return cusExpTempList[activeExpTempIndex];
      } else {
        return null;
      }
    },
    get handling () {
      const { cusExpTempList } = get();
      return cusExpTempList.findIndex(item => item.handling) !== -1;
    }
  },

  // mutations
  setSourceImgFile: (value) => set({ sourceImgFile: value }),
  setSourceImgUrl: (value) => set({ sourceImgUrl: value }),
  setSourceImgBlobUrl: (value) => {
    const { sourceImgBlobUrl } = get();
    if (sourceImgBlobUrl) {
      URL.revokeObjectURL(sourceImgBlobUrl);
    }
    set({ sourceImgBlobUrl: value });
  },
  setSourceImgUploading: (value) => set({ sourceImgUploading: value }),
  setPresetExpTempList: (value) => set({ presetExpTempList: value }),
  setCusExpTempList: (value) => set({ cusExpTempList: value }),
  setCusExpTempUploading: (value) => set({ cusExpTempUploading: value }),
  setTask: (value) => set({ task: value }),
  setGenerating: (value) => set({ generating: value }),
  setActiveExpTempType: (value) => set({ activeExpTempType: value }),
  setActiveExpTempIndex: (value) => set({ activeExpTempIndex: value }),
  setImgEditorOptions: (value) => set({ imgEditorOptions: value }),
  setVideoEditorOptions: (value) => set({ videoEditorOptions: value }),
  setIsMobile: (value) => set({ isMobile: value }),
  setRemoveTipVisible: (value) => set({ removeTipVisible: value }),
  setMoreExpTempVisible: (value) => set({ moreExpTempVisible: value }),
  setIsMute: (value) => set({ isMute: value }),
  setIsPlayResult: (value) => set({ isPlayResult: value }),
  setSafariInfo: (value) => set({ safariInfo: value }),
  
  // actions
  reset: () => set(initialState),
  fetchPresetExpTempList: () => {
    return new Promise((resolve, reject) => {
      getPresetExpTempList()
        .then(res => {
          const { code, data, msg } = res;
          if (code === APICode.Success) {
            set({ presetExpTempList: data || [] });
            resolve();
          } else {
            reject(new Error(msg));
          }
        })
        .catch(reject);
    })
  },
  fetchSignUrl: (extension) => {
    return new Promise((resolve, reject) => {
      getSignUrl(extension)
        .then(res => {
          const { code, data, msg } = res;
          if (code === APICode.Success) {
            resolve(data)
          } else {
            reject(new Error(msg));
          }
        })
        .catch(reject)
    })
  },
  upload: (file, url) => {
    return new Promise((resolve, reject) => {
      putFileTo(file, url)
        .then(() => {
          resolve();
        })
        .catch(reject)
    })
  },
  submitTask: (options) => {
    return new Promise((resolve, reject) => {
      postTask(options)
        .then(res => {
          const { code, data } = res;
          if (code === APICode.Success) {
            resolve(data);
          } else {
            reject(res);
          }
        })
        .catch(reject);
    })
  },
  fetchTask: () => {
    const { task } = get();
    return new Promise((resolve, reject) => {
      getTask(task!.taskId)
        .then(res => {
          const { code, data, msg } = res;
          if (code === APICode.Success) {            
            resolve(data);
          } else {
            reject(new Error(msg));
          }
        })
        .catch(reject);
    })
  },
  generate: async () => {
    try {
      const { 
        sourceImgFile, 
        sourceImgUrl, 
        cusExpTempList,
        activeExpTempType,
        getters: { activeExpTemp } 
      } = get();
      const { 
        fetchSignUrl, 
        upload, 
        submitTask
      } = get();
  
      set({ generating: true });

      // 图片压缩
      let sourceImgFileTemp = sourceImgFile;
      const imgBasicInfo = await getImgDimensions(sourceImgFile!);
      const imgLongerDimension = Math.max(imgBasicInfo.width, imgBasicInfo.height);
      if (imgLongerDimension > IMG_MAX_DEMENSION) {
        sourceImgFileTemp = await scaleImage(sourceImgFile!, IMG_MAX_DEMENSION / imgLongerDimension);
      }

      // 视频压缩
      let expTempVideoTemp = activeExpTemp!.videoFile;
      if (isLocalUrl(activeExpTemp!.video!)) {
        const videoBasicInfo = await getBasicInfoFromVideo(activeExpTemp!.videoFile!);
        const videoLongerDimension = Math.max(videoBasicInfo.width, videoBasicInfo.height);
        if (videoLongerDimension > VIDEO_MAX_DEMENSION) {
          let width = 0;
          let height = 0;
          if (videoBasicInfo.width > VIDEO_MAX_DEMENSION) {
            width = VIDEO_MAX_DEMENSION;
            height = VIDEO_MAX_DEMENSION * (videoBasicInfo.height / videoBasicInfo.width);
          } else {
            width = VIDEO_MAX_DEMENSION * (videoBasicInfo.width / videoBasicInfo.height);
            height = VIDEO_MAX_DEMENSION;
          }

          const { default: FFmpegUtil } = await import("@/utils/FFmpegUtil");

          await FFmpegUtil.init();

          expTempVideoTemp = await FFmpegUtil.handle(
            activeExpTemp!.videoFile!, 
            undefined, 
            undefined, 
            { width, height }
          );
        }
      }

      // 获取签名地址
      const signPs: Promise<TSign>[] = [
        sourceImgUrl 
          ? Promise.resolve({ downloadUrl: sourceImgUrl }) 
          : fetchSignUrl(getExtension(sourceImgFileTemp!)),
        isLocalUrl(activeExpTemp!.video!)
          ? fetchSignUrl(getExtension(expTempVideoTemp!))
          : Promise.resolve({ downloadUrl: activeExpTemp!.video }) 
      ];
      const [imgSign, videoSign] = await Promise.all(signPs);
      
      // 上传
      const uploadPs = [
        sourceImgUrl 
          ? Promise.resolve()
          : upload(sourceImgFileTemp!, imgSign.uploadUrl!),
        isLocalUrl(activeExpTemp!.video!)
          ? upload(expTempVideoTemp!, videoSign.uploadUrl!)
          : Promise.resolve()
      ];
      await Promise.all(uploadPs);

      // 提交任务
      const taskOptions: TTaskOptions = {
        sourceImg: imgSign.downloadUrl!,
        sourceVideo: videoSign.downloadUrl!,
      }
      if (activeExpTempType === ExpTempType.Preset) {
        taskOptions.livePortraitStyleId = activeExpTemp!.id;
      } else if (activeExpTempType === ExpTempType.Cus) {
        taskOptions.customTemplate = true;
        taskOptions.customTemplateName = useUserStore.getState().userInfo?.nickName;
      } else if (activeExpTempType === ExpTempType.Community) {
        const url = new URL(window.location.href);
        const remixSourceId = url.searchParams.get("remixSourceId");
        const nickname = url.searchParams.get("nickname");
        if (remixSourceId) {
          taskOptions.remixSourceId = remixSourceId;
        }
        if (nickname) {
          taskOptions.customTemplateName = nickname;
        }
      }
      let task;
      try {
        task = await submitTask(taskOptions);
      } catch (err: any) {
        if (err.code === APICode.ParamsError) {
          return true;
        } else {
          throw new Error(err.msg);
        }
      }

      // 刷新积分
      const { fetchCredits } = useUserStore.getState();
      fetchCredits();

      // 保存
      set({ task });
      if (!sourceImgUrl) {
        set({ sourceImgUrl: imgSign.downloadUrl });
      }
      if (isLocalUrl(activeExpTemp!.video!)) {
        const expTemp = cusExpTempList.find(item => item.id === activeExpTemp!.id);
        expTemp!.video = videoSign.downloadUrl;
        set({ cusExpTempList: [...cusExpTempList] });
      }
    } catch (err) {
      set({ generating: false })
      throw err;
    }
  },
  loopTask: async (loopQuantity = 0, failedCallback) => {
    let resource_id;
    let resource_name;
    if (MaiDianUtils.expTemp?.id.startsWith(CUS_EXP_TEMP_TITLE_PREFFIX)) {
      resource_id = "customize";
      resource_name = "customize";
    } else if (MaiDianUtils.expTemp?.id.startsWith(COMMUNITY_EXP_TEMP_TITLE_PREFFIX)) {
      const url = new URL(window.location.href);
      resource_id = url.searchParams.get("temp");
      resource_name = "community";
    } else {
      resource_id = MaiDianUtils.expTemp?.id;
      resource_name = MaiDianUtils.expTemp?.title;
    }
    
    try {
      const { fetchTask, loopTask, showRemoveTip, updatePlayState } = get();
      const task = await fetchTask();
      switch (task.status) {
        case TaskStatus.Generating:
          setTimeout(() => {
            loopTask(loopQuantity + 1, failedCallback);
          }, 1 * 1000);
          break;
        case TaskStatus.Success:
          // 埋点（生成结果成功）
          FTAnalytic.addAnalytic({
            type: 'ev',
            action: 'fotor_web$callback$swap_project_results',
            label: {
              is_success: true,
              project_type: PROJECT_TYPE,
              resource_id,
              resource_name,
            },
          });

          set({ 
            task,
            generating: false,
          });
          showRemoveTip();
          updatePlayState(false);
          break;
        case TaskStatus.Failed:
          set({ 
            task,
            generating: false,
          });
          failedCallback?.();

          const { fetchCredits } = useUserStore.getState();
          fetchCredits();

          // 埋点（失败）
          FTAnalytic.addAnalytic({
            type: 'ev',
            action: 'fotor_web$callback$swap_project_results',
            label: {
              is_success: false,
              fail_reason: "task failed",
              project_type: PROJECT_TYPE,
              resource_id,
              resource_name,
            },
          });

          break;
        default:
          // 埋点（未知状态）
          FTAnalytic.addAnalytic({
            type: 'ev',
            action: 'fotor_web$callback$swap_project_results',
            label: {
              is_success: false,
              fail_reason: "unknown status",
              project_type: PROJECT_TYPE,
              resource_id,
              resource_name,
            },
          });

          break;
      }
    } catch (err) {
      set({ generating: false });
      console.error(err);

      // 埋点（报错）
      FTAnalytic.addAnalytic({
        type: 'ev',
        action: 'fotor_web$callback$swap_project_results',
        label: {
          is_success: false,
          fail_reason: "task error",
          project_type: PROJECT_TYPE,
          resource_id,
          resource_name,
        },
      });
    }
  },
  addCusExpTemp: async (file) => {
    const { cusExpTempList } = get();
    const id = getCusExpTempTitle();
    const firstFrameBlob = await getFirstFrame(file);
    set({
      cusExpTempList: [
        ...cusExpTempList,
        { 
          id,
          image: URL.createObjectURL(firstFrameBlob),
          video: URL.createObjectURL(file),
          videoFile: file,
        }
      ],
      activeExpTempIndex: cusExpTempList.length,
      activeExpTempType: ExpTempType.Cus,
    })
  },
  updatePlayState: (isPlay, target) => {
    const { communityExpTempList, presetExpTempList, cusExpTempList } = get();
    communityExpTempList.forEach(item => {
      if (target && item.id === target.id) {
        item.isPlay = isPlay;
      } else {
        item.isPlay = false;
      }
    });
    presetExpTempList.forEach(item => {
      if (target && item.id === target.id) {
        item.isPlay = isPlay;
      } else {
        item.isPlay = false;
      }
    });
    cusExpTempList.forEach(item => {
      if (target && item.id === target.id) {
        item.isPlay = isPlay;
      } else {
        item.isPlay = false;
      }
    });
    set({
      presetExpTempList: [...presetExpTempList],
      cusExpTempList: [...cusExpTempList],
    })
  },
  showRemoveTip: () => {
    const removeTipShowTimesStr = localStorage.getItem(LocalStorageKey.removeTipShowTimes);
    if (removeTipShowTimesStr) {
      const removeTipShowTimeList = removeTipShowTimesStr.split(",");
      if (removeTipShowTimeList.length < 3) {
        const getDateComponents = (timestamp: number) => {
          const date = new Date(timestamp);
          
          const year = date.getFullYear();
          const month = date.getMonth() + 1; // 月份从0开始，因此需要加1
          const dayOfMonth = date.getDate();
        
          return {
            year: year,
            month: month,
            dayOfMonth: dayOfMonth
          };
        }

        const lastTimeStr = removeTipShowTimeList[removeTipShowTimeList.length - 1];
        const lastTime = Number(lastTimeStr);
        const now = Date.now();
        const lastTimeComponent = getDateComponents(lastTime);
        const nowTimeComponent = getDateComponents(now);
        if (
          nowTimeComponent.year > lastTimeComponent.year || 
          (nowTimeComponent.year === lastTimeComponent.year && nowTimeComponent.month > lastTimeComponent.month) || 
          (nowTimeComponent.year === lastTimeComponent.year && nowTimeComponent.month === lastTimeComponent.month && nowTimeComponent.dayOfMonth > lastTimeComponent.dayOfMonth)
        ) {
          set({ removeTipVisible: true });
          removeTipShowTimeList.push(String(now));
          localStorage.setItem(
            LocalStorageKey.removeTipShowTimes, 
            removeTipShowTimeList.join(",")
          );
        }
      }
    } else {
      set({ removeTipVisible: true });
      localStorage.setItem(
        LocalStorageKey.removeTipShowTimes, 
        `${Date.now()}`
      );
    }
  },
  closeVideoEditor: async (option) => {
    if (option) {
      const { task, sourceVideo } = option;
      const id = getCusExpTempTitle();

      task
        .then(async resultVideo => {
          const { cusExpTempList } = get();
          const cusExpTempIndex = cusExpTempList.findIndex(item => item.id === id);
          const cusExpTemp = cusExpTempList[cusExpTempIndex];
          if (cusExpTemp) {
            URL.revokeObjectURL(cusExpTemp.image);
            cusExpTemp.handling = false;
            cusExpTemp.image = URL.createObjectURL(await getFirstFrame(resultVideo));
            cusExpTemp.video = URL.createObjectURL(resultVideo);
            cusExpTemp.videoFile = resultVideo;

            set({
              cusExpTempList: [...cusExpTempList],
              activeExpTempIndex: cusExpTempIndex,
              activeExpTempType: ExpTempType.Cus,
            })
          }
        })
        .catch(console.error)

      const firstFrameBlob = await getFirstFrame(sourceVideo);
      set({
        cusExpTempList: [
          ...get().cusExpTempList,
          { 
            id,
            image: URL.createObjectURL(firstFrameBlob),
            videoFile: sourceVideo,
            handling: true,
            onCancelTask: async () => {
              const { default: FFmpegUtil } = await import("@/utils/FFmpegUtil");
              
              FFmpegUtil.interrupt();

              const { cusExpTempList } = get();
              const index = cusExpTempList.findIndex(item => item.id === id);
              if (index !== -1) {
                cusExpTempList.splice(index, 1);
                set({
                  cusExpTempList: [...cusExpTempList]
                });
              }
            }
          }
        ],
        videoEditorOptions: { show: false }
      })
    } else {
      set({ videoEditorOptions: { show: false } });
    }
  },
  logoutCallback: () => {
    set({
      sourceImgFile: null,
      sourceImgUrl: "",
      sourceImgBlobUrl: "", 
      task: null,
      cusExpTempList: [],
    })
  },
  applyPresetExp: async () => {
    const url = new URL(window.location.href);

    // 获取参数
    const tempParam = "temp";
    const nicknameParam = "nickname";
    const idParam = "id";
    const temp = url.searchParams.get(tempParam); // 模板视频链接
    const nickname = url.searchParams.get(nicknameParam); // 模板所属用户昵称
    const expTempId = url.searchParams.get(idParam);

    if (temp && nickname) {
      const { communityExpTempList } = get();
      const id = getCommunityExpTempTitle();
      const firstFrameBlob = await getFirstFrame(temp);
      set({
        communityExpTempList: [
          ...communityExpTempList,
          { 
            id,
            image: URL.createObjectURL(firstFrameBlob),
            video: temp,
            title: nickname
          }
        ],
        activeExpTempIndex: 0,
        activeExpTempType: ExpTempType.Community,
      });
    } else if (expTempId) {
      const { presetExpTempList, setActiveExpTempIndex } = get();

      const index = presetExpTempList.findIndex(item => item.id === expTempId);
      if (index !== -1) {
        setActiveExpTempIndex(index);
      }
    }
  },
  collect: () => {
    return new Promise((resolve, reject) => {
      const { task } = get();

      if (task) {
        set({ collectLoading: true });

        const finalCollectStatus = task.collectStatus === CollectStatus.Collected ? CollectStatus.UnCollect : CollectStatus.Collected;

        postCollect({ 
          taskIds: task.taskId, 
          collection: finalCollectStatus
        })
          .then(res => {
            const { code, msg } = res;
            if (code === APICode.Success) {
              set({ task: { ...task, collectStatus: finalCollectStatus } });

              resolve(finalCollectStatus);
            } else {
              reject(new Error(msg))
            }
          })
          .catch(reject)
          .finally(() => {
            set({ collectLoading: false });
          })
      } else {
        reject();
      }
    })
  }
}))

export default useCommonStore;